import React, { useCallback, useState } from 'react';

import { Switcher } from 'components/Switcher';
import { RadioCheckbox } from 'components/RadioCheckbox';
import { useMap } from 'contexts/MapContext';
import { Checkbox } from 'components/Checkbox';
import { Select } from 'components/Select';
import { TRAIL_OPTIONS, TrailTimeRange } from 'config/trailOptions';

import { ChevronDownIcon, ChevronUpIcon } from 'assets';

import './styles.scss';

export function MapOptions() {
  const [collapsed, setCollapsed] = useState(false);
  const { state, toggleTrailsEnabled, setActiveTrailOption, toggleColorTrails, setColorTrailsMode, setZoom } = useMap();
  const { trailsEnabled, activeTrailOption, colorTrailsEnabled, colorTrailsMode, zoom } = state;

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const handleEnableToggle = useCallback(() => {
    toggleTrailsEnabled();
  }, [toggleTrailsEnabled]);

  const handleActiveChange = useCallback(
    (value: number | string) => {
      setActiveTrailOption(value as TrailTimeRange);
    },
    [setActiveTrailOption]
  );

  const handleColorTrailsToggle = useCallback(() => {
    toggleColorTrails();
  }, [toggleColorTrails]);

  const handleColorModeChange = useCallback(
    (mode: 'vehicle' | 'length') => {
      setColorTrailsMode(mode);
    },
    [setColorTrailsMode]
  );

  const handleZoomIn = useCallback(() => {
    setZoom(Math.min(zoom + 1, 20)); // Max zoom level 20
  }, [zoom, setZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom(Math.max(zoom - 1, 1)); // Min zoom level 1
  }, [zoom, setZoom]);

  return (
    <>
      <div className="MapOptions-container">
        <div className="MapOptions-header" onClick={handleToggle}>
          {!collapsed ? (
            <ChevronDownIcon className="MapOptions-toggle MapOptions-toggle--active" />
          ) : (
            <ChevronUpIcon className="MapOptions-toggle" />
          )}
          <div className="MapOptions-header-label">Map Options</div>
        </div>
        {!collapsed ? (
          <div className="MapOptions-content">
            <div className="MapOptions-control">
              <div className="MapOptions-control-label" onClick={handleEnableToggle}>
                Vehicle Trails
              </div>
              <Switcher isEnabled={trailsEnabled} onToggle={handleEnableToggle} />
            </div>
            {trailsEnabled && (
              <div className="MapOptions-config">
                <div className="MapOptions-config-item">
                  <div className="MapOptions-config-label">Length</div>
                  <div className="MapOptions-config-value">
                    <Select
                      options={TRAIL_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
                      value={activeTrailOption}
                      onChange={handleActiveChange}
                      placeholder="Select trail length"
                    />
                  </div>
                </div>
                <div className="MapOptions-config-item">
                  <div className="MapOptions-config-label">Color trails</div>
                  <div className="MapOptions-config-value MapOptions-config-value--trial">
                    <div className="MapOptions-config-color">
                      <Checkbox isChecked={colorTrailsEnabled} onChange={handleColorTrailsToggle} />
                    </div>
                    <div
                      className={`MapOptions-config-color ${
                        !colorTrailsEnabled ? 'MapOptions-config-color--disabled' : ''
                      }`}
                    >
                      <RadioCheckbox
                        isChecked={colorTrailsMode === 'vehicle'}
                        onChange={() => handleColorModeChange('vehicle')}
                      />
                      <div className="MapOptions-config-color-label">By vehicle</div>
                    </div>
                    <div
                      className={`MapOptions-config-color ${
                        !colorTrailsEnabled ? 'MapOptions-config-color--disabled' : ''
                      }`}
                    >
                      <RadioCheckbox
                        isChecked={colorTrailsMode === 'length'}
                        onChange={() => handleColorModeChange('length')}
                      />
                      <div className="MapOptions-config-color-label">
                        <div>By elapsed time</div>

                        <div className="MapOptions-tooltip-container">
                          <div className="MapOptions-tooltip-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8.8 12H7.2V7.2H8.8V12ZM8.8 5.6H7.2V4H8.8V5.6Z"
                                fill="#6B7A99"
                              />
                            </svg>
                          </div>
                          <div className="MapOptions-tooltip">
                            <div className="MapOptions-tooltip-item">
                              <div className="MapOptions-tooltip-color" style={{ backgroundColor: '#9B59B6' }}></div>
                              &lt;1 hour
                            </div>
                            <div className="MapOptions-tooltip-item">
                              <div className="MapOptions-tooltip-color" style={{ backgroundColor: '#E74C3C' }}></div>
                              1-4 hours
                            </div>
                            <div className="MapOptions-tooltip-item">
                              <div className="MapOptions-tooltip-color" style={{ backgroundColor: '#F39C12' }}></div>
                              4-8 hours
                            </div>
                            <div className="MapOptions-tooltip-item">
                              <div className="MapOptions-tooltip-color" style={{ backgroundColor: '#27AE60' }}></div>
                              8-12 hours
                            </div>
                            <div className="MapOptions-tooltip-item">
                              <div className="MapOptions-tooltip-color" style={{ backgroundColor: '#3498DB' }}></div>
                              12+ Hours
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="MapOptions-zoom-controls">
        <button className="MapOptions-zoom-button MapOptions-zoom-button--in" onClick={handleZoomIn}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16 9.14286H9.14286V16H6.85714V9.14286H0V6.85714H6.85714V0H9.14286V6.85714H16V9.14286Z"
              fill="#6B7A99"
            />
          </svg>
        </button>
        <button className="MapOptions-zoom-button MapOptions-zoom-button--out" onClick={handleZoomOut}>
          <svg width="16" height="3" viewBox="0 0 16 3" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 3H0V0H16V3Z" fill="#6B7A99" />
          </svg>
        </button>
      </div>
    </>
  );
}
